/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { baseApiService } from './base'
import { usersApiService } from './users'

export interface FormFilter {
  id: number
  name: string
  filter: any
  filterType: string
  filterSchedule?: any
  filterSharing?: any
}

export interface FormFilterResponse {
  filter: {
    createdByUserId: any[]
    updatedByUserId: any[]
    createdAt: {
      instantMatchType: string
      instant: any
    }
    updatedAt: {
      instantMatchType: string
      instant: any
    }
  }
}

class FormFiltersService {
  async loadFromServer(filterId: string | number, filterType: string): Promise<FormFilterResponse> {
    return baseApiService.fetch<FormFilterResponse>(`/api/filters/${filterId}`, {
      params: {
        filterType
      }
    })
  }

  async getById(filterId: string | number): Promise<FormFilter> {
    return baseApiService.fetch<FormFilter>(`/api/filters/${filterId}`)
  }

  async create(
    name: string,
    type: string,
    filter: any,
    meta?: any,
    schedule?: any,
    sharedWith?: any
  ): Promise<FormFilter> {
    // Parse filters based on type (like legacy FormAutoFillFilters.getFiltersParsed)
    const parsedFilter = this.parseFilterByType(type, filter)

    const data = {
      name,
      filter:
        type !== 'EOS_WORKFLOWS_REQUESTS'
          ? this.transformFilter(parsedFilter, meta, type)
          : {
              meta: {},
              filter: {
                studyFolderId: filter?.studyFolderIds?.[0] ?? null,
                studyName: null,
                userId: 0,
                status: null
              }
            },
      filterType: type,
      filterSchedule: this.transformSchedule(schedule),
      filterSharing: this.transformShareList(sharedWith)
    }

    return baseApiService.fetch<FormFilter>('/api/filters/save', {
      method: 'POST',
      body: data
    })
  }

  private parseFilterByType(type: string, filter: any): any {
    if (!filter) return filter

    const parsed = { ...filter }

    switch (type) {
      case 'EOS_WORKFLOWS':
      case 'EOS_ACKNOWLEDGEMENT_ACTIVITY':
      case 'EOS_DOWNLOAD_ACTIVITY':
      case 'EOS_WORKFLOWS_REQUESTS': {
        // Match legacy FormAutoFillFilters.getFiltersParsed for EOS types
        if (parsed.study) {
          parsed.studyFolderId = parsed.study.studyFolderId
        }

        // Convert sites array to siteFolderIds
        if (parsed.level === 'SITE' && parsed.sites) {
          const filteredSites = parsed.sites.filter((i: any) => i?.siteFolderId)
          const pluckedSiteIds = filteredSites.map((i: any) => i.siteFolderId)
          parsed.siteFolderIds = pluckedSiteIds.length ? pluckedSiteIds : null
        } else {
          parsed.siteFolderIds = null
        }

        // Un-wrap countries from [{value: string}] to string[] or null
        if (Array.isArray(parsed.countries)) {
          const filteredCountries = parsed.countries.filter((i: any) => i?.value)
          const pluckedCountries = filteredCountries.map((i: any) => i.value)
          parsed.countries = pluckedCountries.length ? pluckedCountries : null
        } else {
          parsed.countries = null
        }

        delete parsed.sites
        delete parsed.study
        break
      }

      case 'CUSTOM':
        // Handle CUSTOM report specific transformations
        if (parsed.fsiTypeTemplate && Array.isArray(parsed.fsiTypeTemplate)) {
          parsed.fsiTypeTemplate = parsed.fsiTypeTemplate.map((i: any) => (typeof i === 'number' ? i : i.value))
        }
        break

      case 'PROPERTIES_SEARCH':
        // Handle property search specific transformations
        if (parsed.metadataCriteria && Array.isArray(parsed.metadataCriteria)) {
          const transformedCriteria: any[] = []
          parsed.metadataCriteria.forEach((item: any) => {
            if (item.metadataTemplateId || (item.metadataTemplate && item.metadataTemplate.id)) {
              const filterRow: any = {
                metadataTemplateId: item.metadataTemplateId || item.metadataTemplate.id,
                condition: item.condition,
                value: item.value,
                includeInherited: item.includeInherited
              }

              if (item.metadataTemplate && item.metadataTemplate.type === 'DATETIME') {
                if (item.value instanceof Date) {
                  const timezoneOffset = item.value.getTimezoneOffset() * 60000
                  filterRow.value = item.value.setHours(0) - timezoneOffset
                }
              }
              transformedCriteria.push(filterRow)
            }
          })
          parsed.metadataCriteria = transformedCriteria
        }

        if (parsed.tagIds && typeof parsed.tagIds === 'string') {
          try {
            parsed.tagIds = JSON.parse(parsed.tagIds)
          } catch (e) {
            // Keep as is if parse fails
          }
        }

        if (parsed.fsiNameFilter && parsed.fsiNameFilter.value && parsed.fsiNameFilter.condition) {
          parsed.fsiNameFilter = {
            condition: parsed.fsiNameFilter.condition,
            value: parsed.fsiNameFilter.value
          }
        } else {
          parsed.fsiNameFilter = null
        }

        if (parsed.fsiTypeTemplateId && parsed.fsiTypeTemplateId.value) {
          parsed.fsiTypeTemplateId = parsed.fsiTypeTemplateId.value
        }
        break

      case 'CHANGE_REQUEST_TYPES':
        // Handle change request types transformations
        if (parsed.createdAt && parsed.createdAt.instant) {
          const createdAtDate = new Date(parsed.createdAt.instant)
          if (!isNaN(createdAtDate.getTime())) {
            parsed.createdAt.instant = createdAtDate.getTime()
          }
        }
        if (parsed.updatedAt && parsed.updatedAt.instant) {
          const updatedAtDate = new Date(parsed.updatedAt.instant)
          if (!isNaN(updatedAtDate.getTime())) {
            parsed.updatedAt.instant = updatedAtDate.getTime()
          }
        }
        break
    }

    // Remove helper fields (fields starting with help_)
    const cleaned: any = {}
    Object.keys(parsed).forEach((key) => {
      if (!key.startsWith('help_')) {
        cleaned[key] = parsed[key]
      }
    })

    return cleaned
  }

  async update(name: string, id: string | number, schedule?: any, sharedWith?: any): Promise<FormFilter> {
    const data = {
      name,
      filterId: id,
      filterSchedule: this.transformSchedule(schedule),
      filterSharing: this.transformShareList(sharedWith)
    }

    return baseApiService.fetch<FormFilter>('/api/filters/update', {
      method: 'PATCH',
      body: data
    })
  }

  private transformFilter(filter: any, meta: any, type: string): any {
    const filterMetaWrapper = { meta, filter }

    // Add transformation logic based on type if needed
    switch (type) {
      case 'USER':
      case 'USER2':
        // For USER/USER2 type, meta becomes 'sorting' instead of 'meta'
        if (meta) {
          const sorting = { ...meta }
          delete sorting.pageSize
          delete sorting.pageStart
          return {
            filter,
            sorting,
            meta: undefined
          }
        }
        return { filter }
      case 'CHANGE_REQUEST_TYPES':
        return filterMetaWrapper
      default:
        // Match legacy behavior: return just filter when meta is null, otherwise return wrapper
        return meta === null ? filter : filterMetaWrapper
    }
  }

  private transformSchedule(schedule: any): any {
    if (!schedule) {
      return null
    }

    // Clone the schedule to avoid mutating the original
    const transformedSchedule = { ...schedule }

    // Convert date fields to timestamps (match legacy moment().valueOf() behavior)
    if (transformedSchedule.scheduleStartAt) {
      transformedSchedule.scheduleStartAt = new Date(transformedSchedule.scheduleStartAt).getTime()
    }
    if (transformedSchedule.scheduleEndAt) {
      transformedSchedule.scheduleEndAt = new Date(transformedSchedule.scheduleEndAt).getTime()
    }

    return transformedSchedule
  }

  private transformShareList(sharedWith: any): any {
    if (!sharedWith || !Array.isArray(sharedWith)) {
      return null
    }

    return {
      shareGroupIds: sharedWith.filter((x: any) => x.systemHelperType === 'GROUP').map((x: any) => x.id),
      shareUserIds: sharedWith.filter((x: any) => x.systemHelperType === 'USER').map((x: any) => x.id)
    }
  }

  async deleteFilter(
    ids: number | number[],
    type: string,
    filterItem?: any,
    options?: { filter?: any; selectedAll?: boolean },
    modalController?: { showModal: (component: React.ReactNode) => string; hideModal: (id: string) => void },
    toast?: { success: (msg: string) => void; error: (msg: string) => void },
    translate?: (key: string) => string,
    onSuccess?: () => void
  ): Promise<void> {
    const isSelectedAll = options?.selectedAll
    let requestObj: any = { filterIds: Array.isArray(ids) ? ids : [ids] }

    if (isSelectedAll && typeof options?.filter === 'object') {
      requestObj = options.filter
      requestObj.loadAllReportFilters = !!requestObj.loadAllReportFilters
    }

    // If modal controller is provided, show confirmation modal
    if (modalController && translate) {
      return new Promise((resolve, reject) => {
        // Generate type-specific message
        let msg = translate('ARE_YOU_SURE_REMOVE_SELECTED_ITEMS_Q')
        if (type === 'propertiesSearches') {
          msg =
            requestObj.filterIds.length > 1
              ? translate('SAVED_FILTERS_REMOVE_PS_MANY_Q')
              : translate('SAVED_FILTERS_REMOVE_PS_ONE_Q') + '<br><strong>' + filterItem.name + '</strong>'
        } else if (type === 'searches') {
          msg =
            requestObj.filterIds.length > 1
              ? translate('SAVED_FILTERS_REMOVE_SEARCH_MANY_Q')
              : translate('SAVED_FILTERS_REMOVE_SEARCH_ONE_Q') + '<br><strong>' + filterItem.name + '</strong>'
        } else if (type === 'reports') {
          msg =
            requestObj.filterIds.length > 1 || isSelectedAll
              ? translate('SAVED_FILTERS_REMOVE_REPORT_MANY_Q')
              : translate('SAVED_FILTERS_REMOVE_REPORT_ONE_Q') + '<br><strong>' + filterItem.name + '</strong>'
        }

        // Dynamically import ModalInfo to avoid circular dependencies
        import('../../components/ModalInfo').then((module) => {
          const ModalInfo = module.default

          const modalId = modalController.showModal(
            React.createElement(ModalInfo, {
              titleMsg: 'MY_SAVED_SETTINGS_DELETE_TITLE',
              msg: msg,
              modalStatus: () => modalController.hideModal(modalId),
              okFn: async () => {
                try {
                  if (isSelectedAll) {
                    await baseApiService.fetch<void>('/api/filters/deleteall', {
                      method: 'PATCH',
                      body: requestObj
                    })
                  } else {
                    await baseApiService.fetch<void>('/api/filters/delete', {
                      method: 'POST',
                      body: requestObj
                    })
                  }

                  if (toast) {
                    toast.success(translate('MY_SAVED_SETTINGS_DELETE_SUCCESS_MSG'))
                  }

                  if (onSuccess) {
                    onSuccess()
                  }

                  modalController.hideModal(modalId)
                  resolve()
                } catch (error: any) {
                  if (toast) {
                    toast.error(error?.message || 'Failed to delete filter')
                  }
                  modalController.hideModal(modalId)
                  reject(error)
                }
              },
              cancelFn: () => {
                modalController.hideModal(modalId)
                reject(new Error('User cancelled'))
              },
              showCancel: true,
              okTxt: 'YES',
              cancelTxt: 'NO',
              disableOkBtnWhenClicked: true
            })
          )
        })
      })
    }

    // Fallback: direct deletion without modal (backward compatible)
    if (isSelectedAll) {
      return baseApiService.fetch<void>('/api/filters/deleteall', {
        method: 'PATCH',
        body: requestObj
      })
    } else {
      return baseApiService.fetch<void>('/api/filters/delete', {
        method: 'POST',
        body: requestObj
      })
    }
  }

  async getAllSavedReportsFilters(params: any): Promise<{ elements: any[]; totalElements: number }> {
    const usersResponse = await usersApiService.getAssembledUsersGroups()
    const availableUsers = usersResponse.usersAll || []
    const response = await baseApiService.fetch<{ elements: any[]; totalElements: number }>('/api/filters/loadAll', {
      params
    })

    response.elements.forEach((item: any) => {
      item.owner = availableUsers.find((user: any) => user.id === item.ownerId) || {
        firstName: 'NOT_AVAILABLE'
      }
    })

    return response
  }
}

export const formFiltersService = new FormFiltersService()
